import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, CircularProgress, Typography } from "@mui/material";
import {
  useLazyGetMockTestDataQuery,
  useLazyGetPracticeTestDataQuery,
  useSubmitTestAnswersMutation,
  useLazyGetTestAttemptResultsQuery,
  useUpdateTestAttemptMutation,
  useLazyGetInstructionsQuery,
  useAcknowledgeInstructionMutation,
} from "../mockTestSlice";
import { showNotification } from "../../common/headerSlice";
import ReadingSection from "./ReadingSection";
import TestResultsPopup from "./TestResultsPopup";
import { getStoredTimerData, storeTimerData } from "../utils/practiceTimer";
import {
  InstructionWrapper,
  useInstructions,
} from "../../../components/Instructions";

// Helper function to group mcq_multiple questions by questionRange
function groupMcqMultipleQuestions(questions) {
  const grouped = [];
  const mcqMultipleMap = new Map();
  
  questions.forEach((q) => {
    if (q.questionType === 'mcq_multiple' && q.questionRange) {
      if (!mcqMultipleMap.has(q.questionRange)) {
        mcqMultipleMap.set(q.questionRange, []);
      }
      mcqMultipleMap.get(q.questionRange).push(q);
    } else {
      grouped.push(q);
    }
  });
  
  // Add grouped mcq_multiple questions as single items
  mcqMultipleMap.forEach((groupQuestions, range) => {
    if (groupQuestions.length > 0) {
      // Create a grouped question object with all question IDs
      grouped.push({
        ...groupQuestions[0], // Use first question's data as base
        questionIds: groupQuestions.map(q => q.questionId || q._id),
        isGrouped: true,
        groupedQuestions: groupQuestions,
      });
    }
  });
  
  // Sort by questionId to maintain order
  return grouped.sort((a, b) => {
    const aId = a.questionId || a._id || 0;
    const bId = b.questionId || b._id || 0;
    return aId - bId;
  });
}

export default function ReadingPage() {
  const { testId, bundleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  // Detect if this is a bundle test from URL params
  const isBundle = !!bundleId;
  
  // Detect test type from URL path
  const getTestTypeFromPath = () => {
    const path = location.pathname.toLowerCase();
    if (path.includes('/mini-mock-test/')) return 'mini_mock';
    if (path.includes('/class-test/')) return 'class_test';
    return 'full_mock';
  };
  
  const testType = getTestTypeFromPath();
  
  // Helper to get base route for navigation
  const getBaseRoute = () => {
    if (isBundle) {
      return `/app/bundles/${bundleId}/test/${testId}`;
    }
    // Return correct route based on test type
    if (testType === 'mini_mock') {
      return `/mini-mock-test/${testId}`;
    }
    if (testType === 'class_test') {
      return `/class-test/${testId}`;
    }
    return `/mock-test/${testId}`;
  };

  // --- Instruction system integration ---
  const {
    instructionState,
    showInstructionModal,
    hideInstructionModal,
    showConfirmationDialog,
    hideConfirmationDialog,
    showUnattemptedDialog,
    hideUnattemptedDialog,
    showTimeExpiryDialog,
    hideTimeExpiryDialog,
  } = useInstructions();

  // Show Reading instructions at section start
  const [showInitialInstructions, setShowInitialInstructions] = useState(true);
  const [instructionsAcknowledged, setInstructionsAcknowledged] =
    useState(false);
  const [instructionsViewed, setInstructionsViewed] = useState(false);
  const [pendingApiCall, setPendingApiCall] = useState(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [wasInstructionsVisible, setWasInstructionsVisible] = useState(false);

  // Check for essential state and redirect if missing (browser refresh)
  const studentAttemptId =
    location.state?.studentAttemptId ||
    location.state?.mockTest?.studentAttemptId;
  useEffect(() => {
    if (!studentAttemptId || !location.state) {
      console.log(
        "Essential state missing, redirecting to main test screen"
      );
      const redirectRoute = isBundle ? `/app/bundles/${bundleId}` : `${getBaseRoute()}`;
      navigate(redirectRoute, { replace: true });
    }
  }, [studentAttemptId, location.state, navigate, testId, isBundle, bundleId]);

  // Flag to prevent multiple restorations
  // (No longer needed with simplified state management)

  // --- State declarations ---
  const [answers, setAnswers] = useState({}); // Single answers object like listening module
  const [currentPart, setCurrentPart] = useState("A");
  const [currentExtractIndex, setCurrentExtractIndex] = useState(0);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0); // Track current section index for IELTS
  const [readingStudentAnswerId, setReadingStudentAnswerId] = useState("");
  const [localReadingData, setLocalReadingData] = useState(null);
  const [instructionsShownForPart, setInstructionsShownForPart] = useState(
    new Set(["A"])
  ); // Track which parts have shown instructions

  // --- Notes state (persistent across parts/extracts) ---
  const [notes, setNotes] = useState([]);
  const [notesModalOpen, setNotesModalOpen] = useState(false);

  // --- Unattempted questions dialog state ---
  const [pendingAutoNext, setPendingAutoNext] = useState(false);
  const [unattemptedCount, setUnattemptedCount] = useState(0);

  // ...other imports...
  const simulationStep = location.state?.simulationStep; // NEW: just like in ListeningPage
  const isSimulationMode = !location.state?.isPracticeMode && !!simulationStep; // NEW
  const isPracticeMode = location.state?.isPracticeMode;

  // Practice mode timer management - use API response timer if available
  const timerMinutes = location.state?.timerMinutes;
  const selectedSections = location.state?.selectedSections;
  const sectionOrder = location.state?.sectionOrder || [];
  const apiStartTime = location.state?.mockTest?.testStartTime;
  const apiEndTime = location.state?.mockTest?.testEndDate;
  const testAttemptId = location.state?.mockTest?.studentAttemptId;

  // Get stored timer data with test attempt validation
  const storedTimer = getStoredTimerData(testAttemptId);

  // Calculate combined timer start and end times for practice mode
  const [practiceTimerStartTime, setPracticeTimerStartTime] = useState(
    apiStartTime || storedTimer?.startTime || null
  );
  const [practiceTimerEndTime, setPracticeTimerEndTime] = useState(
    apiEndTime || storedTimer?.endTime || null
  );

  // Store timer data in sessionStorage whenever it changes
  useEffect(() => {
    if (practiceTimerStartTime && practiceTimerEndTime) {
      storeTimerData(
        practiceTimerStartTime,
        practiceTimerEndTime,
        testAttemptId
      );
    }
  }, [practiceTimerStartTime, practiceTimerEndTime, testAttemptId]);

  // Results state
  const [testResults, setTestResults] = useState(null);
  const [showResultsPopup, setShowResultsPopup] = useState(false);

  // Listen for end time adjustments from ReadingSection
  useEffect(() => {
    const handleEndTimeAdjustment = (event) => {
      const {
        adjustedEndTime,
        timeSpentOnInstructions,
        totalInstructionTime,
        isExtractChange,
      } = event.detail;
      console.log("Received end time adjustment:", {
        adjustedEndTime,
        timeSpentOnInstructions,
        totalInstructionTime,
        isExtractChange,
      });

      // Update end time with the adjusted value
      setPracticeTimerEndTime(adjustedEndTime);

      console.log(
        `Timer adjusted to compensate for ${timeSpentOnInstructions} seconds spent on instructions`
      );
      console.log(
        `Total time spent on instructions so far: ${totalInstructionTime} seconds`
      );

      // For extract changes, we need to ensure we have enough compensation (at least 10 seconds)
      if (isExtractChange) {
        console.log(
          "This adjustment was for an extract change - ensuring minimum 10 second compensation"
        );
      }

      // Also update in local storage if needed
      if (isPracticeMode && testAttemptId) {
        const storageKey = `reading-practice-timer-${testAttemptId}`;
        const storedTimerData = localStorage.getItem(storageKey);

        if (storedTimerData) {
          const parsedTimer = JSON.parse(storedTimerData);

          // Update the stored timer with the adjusted end time
          const updatedTimer = {
            ...parsedTimer,
            endTime: adjustedEndTime,
            instructionTimeCompensation:
              totalInstructionTime || timeSpentOnInstructions,
            lastAdjustedAt: new Date().toISOString(),
            isExtractChange,
          };

          localStorage.setItem(storageKey, JSON.stringify(updatedTimer));
          console.log(
            "Updated stored timer with adjusted end time:",
            updatedTimer
          );
        }
      }
    };

    window.addEventListener("endTimeAdjusted", handleEndTimeAdjustment);
    return () =>
      window.removeEventListener("endTimeAdjusted", handleEndTimeAdjustment);
  }, [isPracticeMode, testAttemptId]);

  // Timer initialization logic - use backend startTime/endTime from API response
  useEffect(() => {
    // Get startTime and endTime from API response data
    const apiResponseStartTime = localReadingData?.data?.startTime;
    const apiResponseEndTime = localReadingData?.data?.endTime;

    console.log("ReadingPage timer initialization:", {
      apiResponseStartTime,
      apiResponseEndTime,
      hasLocalData: !!localReadingData,
      instructionsViewed,
    });

    // Use API response times if available (highest priority)
    if (apiResponseStartTime && apiResponseEndTime) {
      setPracticeTimerStartTime(apiResponseStartTime);
      setPracticeTimerEndTime(apiResponseEndTime);
      console.log("Using API response timer:", {
        start: apiResponseStartTime,
        end: apiResponseEndTime,
      });
      return;
    }

    // Fallback to legacy approach for backward compatibility
    // Priority: 1. API timer from location state, 2. stored timer, 3. create new timer
    if (apiStartTime && apiEndTime && !practiceTimerStartTime) {
      setPracticeTimerStartTime(apiStartTime);
      setPracticeTimerEndTime(apiEndTime);
    } else if (
      storedTimer &&
      storedTimer.startTime &&
      storedTimer.endTime &&
      !practiceTimerStartTime
    ) {
      setPracticeTimerStartTime(storedTimer.startTime);
      setPracticeTimerEndTime(storedTimer.endTime);
    } else if (
      timerMinutes &&
      !practiceTimerStartTime &&
      !apiStartTime &&
      !storedTimer
    ) {
      const now = new Date();
      const endTime = new Date(now.getTime() + timerMinutes * 60 * 1000);
      setPracticeTimerStartTime(now.toISOString());
      setPracticeTimerEndTime(endTime.toISOString());
    } else if (
      isPracticeMode &&
      !practiceTimerStartTime &&
      !apiStartTime &&
      !storedTimer &&
      !timerMinutes
    ) {
      // Fallback: Create default 60-minute timer for practice mode if no timer is specified
      const now = new Date();
      const endTime = new Date(now.getTime() + 60 * 60 * 1000); // 60 minutes default
      setPracticeTimerStartTime(now.toISOString());
      setPracticeTimerEndTime(endTime.toISOString());
      console.log("Creating default 60-minute practice timer:", {
        startTime: now.toISOString(),
        endTime: endTime.toISOString(),
      });
    }
  }, [
    localReadingData,
    location.state?.isPracticeMode,
    timerMinutes,
    practiceTimerStartTime,
    practiceTimerEndTime,
    apiStartTime,
    apiEndTime,
    storedTimer,
    isPracticeMode,
  ]);

  function extractReadingParts(data) {
    // Handles both OET and IELTS data structures
    if (!data) return [];

    // NEW FORMAT: Direct test.parts array from API (simpler structure)
    // { data: { readingTest: { test: { parts: [...] } } }}
    if (
      data?.data?.readingTest?.test?.parts &&
      Array.isArray(data.data.readingTest.test.parts)
    ) {
      console.log("Detected new simple test.parts format");
      return data.data.readingTest.test.parts.map((part) => ({
        part: part.partName,
        partName: part.partName,
        _id: part._id,
        paragraph: part.paragraph,
        partHeading: part.partHeading,
        heading: part.partHeading,
        textTitle: part.textTitle,
        instructions: part.instructions,
        questionRange: part.questionRange,
        order: part.order,
        // Include any questions if they exist in the part
        questions: part.questions || [],
      }));
    }

    // OET structure: { data: { readingTest: { oetReadingPart: [...] } }}
    if (data?.data?.readingTest?.oetReadingPart) {
      return data.data.readingTest.oetReadingPart;
    }
    if (data?.oetReadingPart) {
      return data.oetReadingPart;
    }

    // IELTS structure: { data: { readingTest: { readingSection: [...] } }}
    if (data?.data?.readingTest?.readingSection) {
      // Transform IELTS sections to parts format
      const sections = data.data.readingTest.readingSection;
      const parts = [];

      sections.forEach((section) => {
        if (section.readingParts && Array.isArray(section.readingParts)) {
          section.readingParts.forEach((part) => {
            parts.push({
              ...part,
              sectionId: section._id,
              sectionName: section.sectionName,
              sectionHeading: section.sectionHeading,
              sectionInstructions: section.instructions,
              sectionQuestionRange: section.sectionQuestionRange,
            });
          });
        }
      });

      return parts;
    }

    return [];
  }

  // API hooks for both simulation and practice modes
  const [triggerGetMockTestData, { data: testData, isLoading, error }] =
    useLazyGetMockTestDataQuery();
  const [
    triggerGetPracticeData,
    {
      data: practiceTestData,
      isLoading: practiceLoading,
      error: practiceError,
    },
  ] = useLazyGetPracticeTestDataQuery();
  const [submitTestAnswers] = useSubmitTestAnswersMutation();

  // Results API
  const [getTestAttemptResults] = useLazyGetTestAttemptResultsQuery();
  const [updateTestAttempt] = useUpdateTestAttemptMutation();

  // Instruction API hooks
  const [
    getInstructions,
    { data: instructionData, isLoading: instructionLoading },
  ] = useLazyGetInstructionsQuery();
  const [acknowledgeInstruction] = useAcknowledgeInstructionMutation();

  // Show Reading Part instructions first, before loading data
  const handleShowInstructions = useCallback(
    (part, extractIndex = null) => {
      const mode = location.state?.isPracticeMode ? "practice" : "simulation";
      const instructionType = part === "A" ? "A" : "BC";
      console.log(
        `Showing instructions for Reading Part ${instructionType} in ${mode} mode`
      );

      // Fetch instructions from API
      if (studentAttemptId) {
        getInstructions({
          studentAttemptId,
          module: "reading",
        });
      }

      showInstructionModal("reading", instructionType, mode);
      setInstructionsViewed(false);
    },
    [
      location.state?.isPracticeMode,
      showInstructionModal,
      studentAttemptId,
      getInstructions,
    ]
  );

  // Track instruction visibility changes
  useEffect(() => {
    if (instructionState.showInstructions) {
      console.log("Instructions became visible");
      setWasInstructionsVisible(true);
    } else if (wasInstructionsVisible && !instructionState.showInstructions) {
      console.log("Instructions closed, executing pending actions");

      // Record the time when instructions closed - important for timer compensation
      const instructionsClosedTime = Date.now();
      console.log(
        `Instructions closed at: ${new Date(
          instructionsClosedTime
        ).toISOString()}`
      );

      // Execute the pending API call if exists
      if (pendingApiCall) {
        console.log("Executing pending API call:", pendingApiCall);

        if (pendingApiCall.type === "practice") {
          triggerGetPracticeData(pendingApiCall.params);
        } else if (pendingApiCall.type === "simulation") {
          triggerGetMockTestData(pendingApiCall.params);
        } else if (pendingApiCall.type === "navigation") {
          // Handle navigation after instructions are closed
          console.log(
            "Navigating after instructions closed:",
            pendingApiCall.params
          );
          navigate(pendingApiCall.params.path, {
            state: pendingApiCall.params.state,
          });
        }

        // Clear the pending API call
        setPendingApiCall(null);
      }

      // Ensure the timer is properly compensated for instruction time
      if (practiceTimerEndTime) {
        console.log(
          "Timer end time after instructions closed:",
          practiceTimerEndTime
        );
      }

      // Mark instructions as viewed - this will trigger timer initialization
      console.log(
        "Marking instructions as viewed, timer will now initialize/resume"
      );
      setInstructionsViewed(true);
      setWasInstructionsVisible(false);
    }
  }, [
    instructionState.showInstructions,
    wasInstructionsVisible,
    pendingApiCall,
    triggerGetPracticeData,
    triggerGetMockTestData,
    navigate,
    practiceTimerEndTime,
  ]);

  // Show initial instructions when component first loads
  useEffect(() => {
    if (showInitialInstructions && isFirstLoad) {
      handleShowInstructions("A");
      setShowInitialInstructions(false);
      setIsFirstLoad(false);
    }
  }, [showInitialInstructions, isFirstLoad, handleShowInstructions]);

  // Show Part BC instructions when transitioning from Part A to B or B to C
  const [lastPart, setLastPart] = useState(null);
  useEffect(() => {
    if (
      currentPart === "B" &&
      lastPart === "A" &&
      instructionsViewed &&
      !instructionsShownForPart.has("B")
    ) {
      console.log("Showing instructions for Part B (first time)");
      handleShowInstructions("B");
      setInstructionsShownForPart((prev) => new Set([...prev, "B"]));
    } else if (
      currentPart === "C" &&
      lastPart === "B" &&
      instructionsViewed &&
      !instructionsShownForPart.has("C")
    ) {
      console.log("Showing instructions for Part C (first time)");
      handleShowInstructions("C");
      setInstructionsShownForPart((prev) => new Set([...prev, "C"]));
    }
    setLastPart(currentPart);
  }, [
    currentPart,
    lastPart,
    instructionsViewed,
    handleShowInstructions,
    instructionsShownForPart,
  ]);

  // Helper function to determine if this is the final section in practice mode
  const isFinalSection = () => {
    const selectedSections = location.state?.selectedSections;
    if (!selectedSections) return false;

    // Check if reading is actually selected - if not, we shouldn't be here
    if (!selectedSections.reading) return false;

    // Check if this is the last section in practice mode
    // If only reading is selected, or if we've completed reading and no writing is selected
    const hasWriting = selectedSections.writing;

    // If no writing section is selected after reading, this is final
    return !hasWriting;
  };

  // Helper function to show results for practice mode
  const showPracticeResults = async (studentAttemptId) => {
    try {
      console.log(
        "Fetching practice test results for studentAttemptId:",
        studentAttemptId
      );
      const resultsResponse = await getTestAttemptResults(
        studentAttemptId
      ).unwrap();
      console.log("Practice test results:", resultsResponse);
      setTestResults(resultsResponse);
      setShowResultsPopup(true);
    } catch (resultsError) {
      console.error("Error fetching test attempt results:", resultsError);
      // Show user-friendly error message
      dispatch(
        showNotification({
          message: "Error fetching results. Please try again.",
          status: 0,
        })
      );
    }
  };

  // Determine which loading states and errors to use based on practice mode
  const currentLoading = isPracticeMode ? practiceLoading : isLoading;
  const currentError = isPracticeMode ? practiceError : error;
  useEffect(() => {
    // Only clear answers when changing between different parts, not extracts within same part
    if (isSimulationMode && location.state?.simulationStep) {
      console.log("=== SIMULATION STEP EFFECT ===");
      console.log("simulationStep:", location.state.simulationStep);
      console.log("localReadingData exists:", !!localReadingData);

      // For IELTS structure, use readingSectionIds directly
      const readingSectionIds =
        location.state.simulationStep.readingSectionIds || [];
      const readingPartIndex =
        location.state.simulationStep.readingPartIndex || 0;

      console.log("Simulation step - using section IDs:", {
        readingSectionIds,
        readingPartIndex,
      });

      if (
        readingSectionIds.length > 0 &&
        readingPartIndex < readingSectionIds.length
      ) {
        const currentSectionId = readingSectionIds[readingPartIndex];
        console.log("Setting currentPart to section ID:", currentSectionId);
        setCurrentPart(currentSectionId);
      } else {
        // Fallback: try to extract parts from data
        const parts = extractReadingParts(localReadingData);
        console.log(
          "Fallback - extracted parts:",
          parts?.map((p) => p.part || p._id || p.sectionId)
        );

        if (parts && parts[readingPartIndex]) {
          const newPart =
            parts[readingPartIndex].part ||
            parts[readingPartIndex]._id ||
            parts[readingPartIndex].sectionId;
          console.log(
            "Setting currentPart to:",
            newPart,
            "from readingPartIndex:",
            readingPartIndex
          );
          setCurrentPart(newPart);
        } else if (parts && parts.length > 0) {
          // Final fallback: use first available part
          const firstPart = parts[0].part || parts[0]._id || parts[0].sectionId;
          console.log(
            "No part found for readingPartIndex, using first available part:",
            firstPart
          );
          setCurrentPart(firstPart);
        } else {
          console.log("No parts found, keeping currentPart as:", currentPart);
        }
      }

      console.log("=== END SIMULATION STEP EFFECT ===");
    }
  }, [
    location.state?.currentExtractIndex,
    location.state?.simulationStep,
    isSimulationMode,
    localReadingData,
    currentPart, // Added currentPart to track part changes
  ]);

  // Helper function to schedule API calls after instructions close
  const scheduleApiCall = useCallback((apiParams, type = "practice") => {
    console.log(
      `Scheduling ${type} API call to run after instructions close:`,
      apiParams
    );
    setPendingApiCall({
      type,
      params: apiParams,
    });
  }, []);

  // Fetch API data based on mode and navigation state
  useEffect(() => {
    const practiceDataFromState = location.state?.practiceData;

    // 1. Always update currentPart based on location.state for IELTS practice mode
    // Only do this on initial load, not during section transitions
    if (
      isPracticeMode &&
      !practiceDataFromState?.data?.readingTest?.oetReadingPart &&
      !localReadingData
    ) {
      const readingPartsOrder = location.state?.readingPartsOrder || [];
      const currentReadingPartIndex =
        location.state?.currentReadingPartIndex || 0;
      if (
        readingPartsOrder.length > 0 &&
        currentReadingPartIndex < readingPartsOrder.length
      ) {
        const newCurrentPart = readingPartsOrder[currentReadingPartIndex];
        console.log("Setting currentPart from location.state (initial load):", {
          currentReadingPartIndex,
          newCurrentPart,
          readingPartsOrder,
        });
        setCurrentPart(newCurrentPart);
        setCurrentSectionIndex(currentReadingPartIndex);
      }
    }

    // 2. Set localReadingData from navigation state (new data or first time)
    if (practiceDataFromState && isPracticeMode) {
      // Always update localReadingData if we have new practiceData from navigation
      if (!localReadingData || practiceDataFromState !== localReadingData) {
        console.log("Updating localReadingData from navigation:", {
          hasExistingData: !!localReadingData,
          newDataPresent: !!practiceDataFromState,
        });
        setLocalReadingData(practiceDataFromState);
      }

      // Set part and extract as well for OET
      if (practiceDataFromState?.data?.readingTest?.oetReadingPart) {
        const availableParts =
          practiceDataFromState.data.readingTest.oetReadingPart;
        if (availableParts.length > 0) {
          const firstPart = availableParts[0].part;
          setCurrentPart(firstPart);
          setCurrentExtractIndex(0);
          // Keep existing answers - never clear like listening module
        }
      }
      return; // Don't continue, data set from navigation
    }

    // 3. Don't fetch data from API until instructions are acknowledged
    if (!instructionsAcknowledged) {
      return;
    }

    // 4. Early guard: only proceed if not already loading or already has data
    if (
      (isPracticeMode ? practiceLoading : isLoading) ||
      localReadingData ||
      pendingApiCall
    )
      return;

    // 5. Now run your original API logic
    const studentAttemptId = location.state?.studentAttemptId;

    if (!studentAttemptId) {
      console.error("No studentAttemptId found in navigation state!");
      return;
    }

    // This check is now redundant but for safety (after above guard):
    if (practiceDataFromState && isPracticeMode) {
      return;
    }

    // We now only prepare the API call parameters but don't call APIs directly
    console.log("=== READING DATA FETCH DEBUG ===");
    console.log("isPracticeMode:", isPracticeMode);
    console.log("isSimulationMode:", isSimulationMode);
    console.log("simulationStep:", simulationStep);
    console.log(
      "location.state?.simulationStep:",
      location.state?.simulationStep
    );
    console.log("instructionsViewed:", instructionsViewed);
    console.log("=== END DEBUG ===");

    if (isPracticeMode) {
      const readingPartsOrder = location.state?.readingPartsOrder || [];
      const readingQuestionsOrder = location.state?.readingQuestionsOrder || [];
      const currentReadingPartIndex =
        location.state?.currentReadingPartIndex || 0;

      if (currentReadingPartIndex < readingPartsOrder.length) {
        const currentPartId = readingPartsOrder[currentReadingPartIndex];
        const apiParams = {
          studentAttemptId,
          currentSection: "reading",
          readingId: location.state?.readingTestId,
          readingSectionId: currentPartId,
          readingQuestionIds: readingQuestionsOrder,
        };

        // Only directly call API if instructions already viewed, otherwise schedule
        if (instructionsViewed) {
          console.log(
            "Instructions already viewed, calling API directly:",
            apiParams
          );
          triggerGetPracticeData(apiParams);
        } else {
          scheduleApiCall(apiParams, "practice");
        }
      } else {
        const apiParams = {
          studentAttemptId,
          currentSection: "reading",
          readingId: location.state?.readingTestId,
          readingSectionId: location.state?.readingPartIds?.[0],
          readingQuestionIds: location.state?.readingQuestionIds,
        };

        if (instructionsViewed) {
          console.log(
            "Instructions already viewed, calling API directly:",
            apiParams
          );
          triggerGetPracticeData(apiParams);
        } else {
          scheduleApiCall(apiParams, "practice");
        }
      }
    } else if (simulationStep || isSimulationMode) {
      // SIMULATION MODE (step by step), just like Listening
      console.log(
        "SIMULATION MODE: Preparing reading data fetch with simulationStep:",
        simulationStep
      );
      console.log("SIMULATION MODE: isSimulationMode:", isSimulationMode);
      console.log("SIMULATION MODE: location.state:", location.state);

      const readingSectionIds = simulationStep?.readingSectionIds || [];
      const readingPartIndex = simulationStep?.readingPartIndex || 0;
      const readingTestId = location.state?.readingTestId;

      console.log("SIMULATION MODE: Reading section details:", {
        readingSectionIds,
        readingPartIndex,
        readingTestId,
        totalReadingSections: readingSectionIds.length,
      });

      // For SIMULATION mode, use readingSectionIds (new structure)
      const currentSectionId = readingSectionIds[readingPartIndex];

      if (!readingTestId) {
        console.error("SIMULATION MODE: No readingTestId found!");
        console.error(
          "SIMULATION MODE: Available location.state keys:",
          Object.keys(location.state || {})
        );
        return;
      }

      // If no specific section IDs, use readingTestId as fallback
      const apiParams = {
        studentAttemptId,
        currentSection: "reading",
        readingId: readingTestId,
        readingSectionId: currentSectionId || readingTestId, // Fallback to readingTestId if no sections
      };

      console.log("SIMULATION MODE: API params for reading:", apiParams);

      if (instructionsViewed) {
        console.log(
          "SIMULATION MODE: Instructions already viewed, calling API directly"
        );
        triggerGetPracticeData(apiParams);
      } else {
        console.log("SIMULATION MODE: Scheduling API call after instructions");
        scheduleApiCall(apiParams, "practice");
      }
    } else {
      // Old simulation fallback, for compatibility if needed (optional)
      const apiParams = {
        studentAttemptId,
        currentPart,
        isNextPart: true,
      };

      if (instructionsViewed) {
        console.log(
          "Instructions already viewed, calling API directly:",
          apiParams
        );
        triggerGetMockTestData(apiParams);
      } else {
        scheduleApiCall(apiParams, "simulation");
      }
    }
  }, [
    testId,
    location.state?.studentAttemptId,
    location.state?.practiceData,
    location.state?.currentReadingPartIndex,
    location.state?.isPracticeMode,
    location.state?.readingTestId,
    location.state?.readingPartIds,
    location.state?.readingQuestionIds,
    location.state?.readingPartsOrder,
    location.state?.readingQuestionsOrder,
    currentPart,
    triggerGetMockTestData,
    triggerGetPracticeData,
    isPracticeMode,
    localReadingData,
    practiceLoading,
    isLoading,
    simulationStep,
    instructionsViewed,
    instructionsAcknowledged,
    pendingApiCall,
    scheduleApiCall,
  ]);

  // Update localReadingData when API data arrives
  useEffect(() => {
    if (practiceTestData || testData) {
      const apiData = practiceTestData || testData;
      console.log("=== API DATA ARRIVED ===");
      console.log("API data:", apiData);
      console.log("Setting localReadingData");
      console.log("Current currentPart before setting:", currentPart);

      setLocalReadingData(apiData);

      // ---- ENHANCED PART SETTING LOGIC ----
      if (apiData?.data?.readingTest?.oetReadingPart) {
        // OET structure
        const availableParts = apiData.data.readingTest.oetReadingPart;
        console.log("OET structure detected, available parts:", availableParts);
        // If currentPart does not exist, set it to the first available part
        if (!availableParts.some((p) => p.part === currentPart)) {
          console.log(
            "Setting currentPart to first OET part:",
            availableParts[0].part
          );
          setCurrentPart(availableParts[0].part);
          setCurrentExtractIndex(0);
          // Keep existing answers - never clear like listening module
        }
      } else {
        // Check for IELTS structure or other structures
        const parts = extractReadingParts(apiData);
        console.log("Non-OET structure, extracted parts:", parts);

        // Check if this is IELTS structure
        const isIeltsStructure = apiData?.data?.readingTest?.readingSection;

        if (isIeltsStructure) {
          console.log("IELTS structure detected in API data processing");
          const sections = apiData.data.readingTest.readingSection;

          if (sections && sections.length > 0) {
            // For IELTS, currentPart should be the section ID, not part ID
            const firstSectionId = sections[0]._id;
            console.log(
              "Setting currentPart to first section ID:",
              firstSectionId
            );
            setCurrentPart(firstSectionId);
            setCurrentExtractIndex(0);
          }
        } else if (parts && parts.length > 0) {
          // Non-IELTS structure (OET, etc.)
          const currentPartExists = parts.some(
            (p) =>
              p.part === currentPart ||
              p._id === currentPart ||
              p.sectionId === currentPart
          );

          if (!currentPartExists) {
            // Set to first available part
            const firstPart =
              parts[0].part || parts[0]._id || parts[0].sectionId || "A";
            console.log(
              "Current part doesn't exist, setting to first part:",
              firstPart
            );
            setCurrentPart(firstPart);
            setCurrentExtractIndex(0);
          } else {
            console.log("Current part exists in data:", currentPart);
          }
        }
      }

      console.log("=== END API DATA PROCESSING ===");
    }
  }, [practiceTestData, testData, currentPart]);

  // Update currentExtractIndex when location.state changes (simulation mode only)
  useEffect(() => {
    if (
      isSimulationMode &&
      typeof location.state?.currentExtractIndex === "number"
    ) {
      console.log("Updating currentExtractIndex from location.state:", {
        from: currentExtractIndex,
        to: location.state.currentExtractIndex,
      });
      setCurrentExtractIndex(location.state.currentExtractIndex);
    }
  }, [
    location.state?.currentExtractIndex,
    currentExtractIndex,
    isSimulationMode,
  ]);

  const handleAnswerChange = (questionId, value) => {
    const mode = isSimulationMode ? "SIMULATION" : "PRACTICE";
    console.log(
      `[${mode}] Answer changed for question ${questionId}: ${value} in Part ${currentPart}`
    );

    // Simple answer update like listening module
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));

    console.log(`[${mode}] Updated answers:`, {
      ...answers,
      [questionId]: value,
    });
  };

  // Handler for adding notes - persists across parts/extracts
  const handleAddNote = (noteData) => {
    const newNote = {
      selectedText: noteData.selectedText,
      userNote: noteData.userNote,
      timestamp: new Date().toLocaleTimeString(),
      id: Date.now(),
      part: currentPart, // Track which part the note was created in
      extractName: `Part ${currentPart}${
        currentExtractIndex > 0 ? ` Extract ${currentExtractIndex + 1}` : ""
      }`,
    };
    setNotes((prev) => [...prev, newNote]);
  };

  // Helper to get ALL question IDs from the entire reading test
  const getAllQuestionIdsFromTest = () => {
    const parts = extractReadingParts(localReadingData);
    const allQuestionIds = [];

    parts.forEach((part) => {
      if (part.questions && Array.isArray(part.questions)) {
        part.questions.forEach((q) => {
          if (q.questionId != null) {
            allQuestionIds.push(q.questionId);
          }
        });
      }
    });

    return allQuestionIds;
  };

  // Helper to get all question IDs for the current reading part
  const getAllQuestionIdsForCurrentPart = () => {
    const parts = extractReadingParts(localReadingData);
    const currentPartData = parts.find((p) => p.part === currentPart);

    if (!currentPartData || !currentPartData.questions) {
      return [];
    }

    let questionIds = [];

    if (currentPart === "A") {
      // Part A: All questions in the part
      questionIds = currentPartData.questions.map((q) => String(q.questionId));
    } else if (currentPart === "B") {
      // Part B: Single question for current extract
      const currentExtractIdx = simulationExtractIndex;

      if (isPracticeMode) {
        // In practice mode, questions are already filtered, so directly use the index
        const currentQuestion = currentPartData.questions[currentExtractIdx];
        if (currentQuestion) {
          questionIds = [String(currentQuestion.questionId)];
        }
      } else {
        // Simulation mode: use original logic
        const currentQuestion = currentPartData.questions[currentExtractIdx];
        if (currentQuestion) {
          questionIds = [String(currentQuestion.questionId)];
        }
      }
    } else if (currentPart === "C") {
      // Part C: Questions for current extract
      const allQuestions = currentPartData.questions || [];
      const currentExtractIdx = simulationExtractIndex;

      if (isPracticeMode) {
        // In practice mode, group questions by extract and get current extract's questions
        const questionsByExtract = {};
        allQuestions.forEach((q) => {
          const extractId = q.readingTextId;
          if (!questionsByExtract[extractId]) {
            questionsByExtract[extractId] = [];
          }
          questionsByExtract[extractId].push(q);
        });

        const uniqueExtractIds = Object.keys(questionsByExtract);
        const currentExtractId = uniqueExtractIds[currentExtractIdx];

        if (currentExtractId && questionsByExtract[currentExtractId]) {
          questionIds = questionsByExtract[currentExtractId].map((q) =>
            String(q.questionId)
          );
        }
      } else {
        // Simulation mode: use original logic
        const uniqueExtractIds = [
          ...new Set(allQuestions.map((q) => q.readingTextId)),
        ];
        const currentExtractId = uniqueExtractIds[currentExtractIdx];

        if (currentExtractId) {
          const extractQuestions = allQuestions.filter(
            (q) => q.readingTextId === currentExtractId
          );
          questionIds = extractQuestions.map((q) => String(q.questionId));
        }
      }
    }

    return questionIds;
  };
  // Add in ReadingPage.jsx
  const handleJumpToExtract = useCallback(
    (part, extractIndex) => {
      if (isPracticeMode) {
        // Practice: local state drives the view
        console.log(
          `Navigating to extract ${extractIndex + 1} within current section`
        );
        setCurrentPart(part);
        setCurrentExtractIndex(extractIndex);
        return;
      }
      // Simulation: navigate with state so the loader fetches the right extract
      navigate(`${getBaseRoute()}/reading`, {
        state: {
          ...location.state,
          simulationStep: location.state?.simulationStep,
          currentExtractIndex: extractIndex,
          practiceData: null,
        },
      });
    },
    [isPracticeMode, navigate, location.state, testId]
  );

  // Helper function to handle part transitions with instructions
  const handlePartTransition = (nextPart) => {
    console.log(`Transitioning to part ${nextPart}`);

    // Just change the part - instructions will be shown automatically by the useEffect
    setCurrentPart(nextPart);
    setCurrentExtractIndex(0);
  };

  const handleAutoNext = async () => {
    await proceedAutoNext();
  };

  // Actual logic to proceed to next part/section
  const proceedAutoNext = async () => {
    console.log("=== HANDLE AUTO NEXT CALLED ===");
    console.log("Current part completed:", currentPart, "answers:", answers);
    console.log("isSimulationMode:", isSimulationMode);
    console.log("isPracticeMode:", isPracticeMode);
    console.log("simulationStep:", location.state?.simulationStep);
    console.log("=== END HANDLE AUTO NEXT HEADER ===");

    const studentAttemptId = location.state?.studentAttemptId;

    // Helper to extract reading parts array safely for both OET and IELTS
    const extractReadingPartsHelper = (data) => extractReadingParts(data);

    try {
      // Submit ALL questions with answers or empty strings
      // Get readingTestId from API data if available
      let readingTestId = testId;
      if (localReadingData?.data?.readingTest?._id) {
        readingTestId = localReadingData.data.readingTest._id;
      }

      // Get all question IDs from the test
      const allQuestionIds = getAllQuestionIdsFromTest();

      // Build answers array with all questions
      const answersArray = allQuestionIds.map((questionId) => {
        const givenAnswer = answers?.[questionId] ?? "";
        return {
          questionId: isNaN(Number(questionId)) ? questionId : Number(questionId),
          givenAnswer: givenAnswer,
        };
      });

      const submissionData = {
        testId: readingTestId,
        testAttemptId: studentAttemptId,
        answers: answersArray,
        ...(readingStudentAnswerId &&
          typeof readingStudentAnswerId === "string" &&
          readingStudentAnswerId.trim() !== "" && {
            studentAnswerId: readingStudentAnswerId,
          }),
        isNextPart: true,
      };

      console.log("Submitting answers:", submissionData);
      const response = await submitTestAnswers(submissionData).unwrap();
      console.log("Submission response:", response);

      // Update studentAnswerId for next section
      const newStudentAnswerId =
        response?.studentAnswerId ||
        response?.data?.studentAnswerId ||
        response?.data?.data?.studentAnswerId;

      if (newStudentAnswerId) {
        setReadingStudentAnswerId(newStudentAnswerId);
      }

      // ----------------- PRACTICE MODE FLOW -----------------
      if (isPracticeMode) {
        const readingPartsOrder = location.state?.readingPartsOrder || [];
        const readingQuestionsOrder =
          location.state?.readingQuestionsOrder || [];
        // Use state variable for current section index, fallback to location.state
        let currentReadingPartIndex = currentSectionIndex;
        if (
          currentReadingPartIndex === 0 &&
          location.state?.currentReadingPartIndex
        ) {
          currentReadingPartIndex = location.state.currentReadingPartIndex;
        }

        // Find the current section index based on currentPart ID as final fallback
        const actualCurrentIndex = readingPartsOrder.findIndex(
          (sectionId) => sectionId === currentPart
        );
        if (
          actualCurrentIndex !== -1 &&
          actualCurrentIndex !== currentReadingPartIndex
        ) {
          currentReadingPartIndex = actualCurrentIndex;
          setCurrentSectionIndex(actualCurrentIndex); // Update state to match
          console.log("Updated currentReadingPartIndex based on currentPart:", {
            currentPart,
            foundIndex: actualCurrentIndex,
            readingPartsOrder,
          });
        }

        const parts = extractReadingParts(localReadingData);

        // Check if this is IELTS structure
        const isIeltsStructure = parts.some((p) => p.sectionId && p.paragraph);

        if (isIeltsStructure) {
          // For IELTS, check if we need to move to the next part within the same section
          const currentSectionParts = parts.filter(
            (p) => p.sectionId === currentPart
          );

          console.log("IELTS auto-next logic:", {
            currentPart,
            currentExtractIndex,
            totalPartsInSection: currentSectionParts.length,
            partNames: currentSectionParts.map((p) => p.partName),
          });

          if (currentExtractIndex < currentSectionParts.length - 1) {
            // Move to next part within the same section WITHOUT showing instructions
            const nextExtractIndex = currentExtractIndex + 1;
            console.log(
              `IELTS: Moving to part ${currentSectionParts[nextExtractIndex].partName} (extract ${nextExtractIndex}) within same section`
            );

            // Don't clear answers when moving within same section - keep them
            setCurrentExtractIndex(nextExtractIndex);
            return;
          }

          // All parts in current section completed, move to next reading section
          const nextReadingPartIndex = currentReadingPartIndex + 1;
          if (nextReadingPartIndex < readingPartsOrder.length) {
            try {
              const nextPartPracticeData = await triggerGetPracticeData({
                studentAttemptId,
                currentSection: "reading",
                readingId: location.state?.readingTestId,
                readingSectionId: readingPartsOrder[nextReadingPartIndex],
                readingQuestionIds: readingQuestionsOrder,
              }).unwrap();

              console.log("IELTS: Navigating to next section", {
                fromSection: currentPart,
                toSection: readingPartsOrder[nextReadingPartIndex],
                nextReadingPartIndex,
              });

              // In practice mode, handle section transition locally without navigation
              if (isPracticeMode) {
                console.log(
                  "IELTS Practice: Updating local state for next section"
                );
                console.log(
                  "Transitioning from section:",
                  currentPart,
                  "to section:",
                  readingPartsOrder[nextReadingPartIndex]
                );
                console.log(
                  "Section index transition:",
                  currentReadingPartIndex,
                  "→",
                  nextReadingPartIndex
                );

                // Update local reading data with new section data
                setLocalReadingData(nextPartPracticeData);
                // Update current part to the new section ID
                const newSectionId = readingPartsOrder[nextReadingPartIndex];
                setCurrentPart(newSectionId);
                // Update current section index
                setCurrentSectionIndex(nextReadingPartIndex);
                // Reset to first extract of new section
                setCurrentExtractIndex(0);

                console.log(
                  "Section transition completed - new currentPart:",
                  newSectionId,
                  "index:",
                  nextReadingPartIndex
                );
                return; // Don't continue to navigation
              } else {
                navigate(`${getBaseRoute()}/reading`, {
                  state: {
                    ...location.state,
                    currentReadingPartIndex: nextReadingPartIndex,
                    practiceData: nextPartPracticeData,
                    currentExtractIndex: 0, // Reset to Part A when moving to new section
                  },
                });
              }
              return;
            } catch (error) {
              console.error("Error fetching next IELTS section data:", error);
              dispatch(
                showNotification({
                  message: "Error loading next section. Please try again.",
                  status: 0,
                })
              );
              return;
            }
          } else {
            // No more sections, proceed to next module or show results
            navigate(`${getBaseRoute()}/writing`, {
              state: {
                ...location.state,
                studentAnswerId: readingStudentAnswerId,
              },
            });
            return;
          }
        }

        // OET logic (existing)
        const currentPartData = parts.find((p) => p.part === currentPart);

        if (currentPart === "B") {
          const totalSelectedQuestions =
            currentPartData?.questions?.length || 1;
          if (currentExtractIndex < totalSelectedQuestions - 1) {
            // Move to next extract in Part B WITHOUT showing instructions
            const nextExtractIndex = currentExtractIndex + 1;
            console.log(
              `Moving to Part B extract ${nextExtractIndex} without instructions`
            );

            // Don't clear answers when moving within same part - keep them
            setCurrentExtractIndex(nextExtractIndex);
            return;
          }
        } else if (currentPart === "C") {
          const allQuestions = currentPartData?.questions || [];
          const uniqueExtractIds = [
            ...new Set(allQuestions.map((q) => q.readingTextId)),
          ];
          if (currentExtractIndex < uniqueExtractIds.length - 1) {
            // Move to next extract in Part C WITHOUT showing instructions
            const nextExtractIndex = currentExtractIndex + 1;
            console.log(
              `Moving to Part C extract ${nextExtractIndex} without instructions`
            );

            // Don't clear answers when moving within same part - keep them
            setCurrentExtractIndex(nextExtractIndex);
            return;
          }
        }

        // All extracts in current part completed, move to next reading part
        const nextReadingPartIndex = currentReadingPartIndex + 1;
        if (nextReadingPartIndex < readingPartsOrder.length) {
          try {
            const nextPartPracticeData = await triggerGetPracticeData({
              studentAttemptId,
              currentSection: "reading",
              readingId: location.state?.readingTestId,
              readingSectionId: readingPartsOrder[nextReadingPartIndex],
              readingQuestionIds: readingQuestionsOrder,
            }).unwrap();

            navigate(`${getBaseRoute()}/reading`, {
              state: {
                ...location.state,

                currentReadingPartIndex: nextReadingPartIndex,
                practiceData: nextPartPracticeData,
                studentAnswerId: readingStudentAnswerId,
                isPracticeMode: true,
              },
            });
            return;
          } catch (error) {
            console.error("Error fetching next reading part data:", error);
          }
        }

        // All reading parts completed, check if this is the final section or move to next section
        const selectedSections = location.state?.selectedSections;

        if (isFinalSection()) {
          // This is the final section - show results
          await showPracticeResults(studentAttemptId);
        } else if (selectedSections?.writing) {
          navigate(`${getBaseRoute()}/writing`, {
            state: {
              ...location.state,
              studentAnswerId: readingStudentAnswerId,
              practiceData: null,
            },
          });
        } else {
          // Fallback - fetch results and show popup
          await showPracticeResults(studentAttemptId);
        }
      }

      // ----------------- SIMULATION MODE FLOW -----------------
      else if (location.state?.simulationStep) {
        const simulationStep = location.state.simulationStep;
        const readingPartIds = simulationStep.readingPartIds || [];
        const readingPartIndex = simulationStep.readingPartIndex || 0;
        const totalReadingParts =
          simulationStep.totalReadingParts || readingPartIds.length;

        const parts = extractReadingParts(localReadingData);
        const currentPartData = parts.find((p) => p.part === currentPart);

        // Always use navigation state for extract index if present
        const currentExtractIdx =
          typeof location.state.currentExtractIndex === "number"
            ? location.state.currentExtractIndex
            : currentExtractIndex;

        // Handle extract navigation for Parts B and C (same as practice mode)
        console.log(
          "Simulation mode handleAutoNext - currentPart:",
          currentPart,
          "currentExtractIdx:",
          currentExtractIdx
        );

        if (currentPart === "B") {
          const totalQuestions = currentPartData?.questions?.length || 1;
          console.log(
            "Part B - totalQuestions:",
            totalQuestions,
            "currentExtractIdx:",
            currentExtractIdx
          );

          if (currentExtractIdx < totalQuestions - 1) {
            console.log(
              "Navigating to next extract in Part B:",
              currentExtractIdx + 1
            );
            // Keep all answers preserved - never clear like listening module

            // Navigate directly to next extract WITHOUT showing instructions
            navigate(`${getBaseRoute()}/reading`, {
              state: {
                ...location.state,
                simulationStep: { ...simulationStep, readingPartIndex },
                currentExtractIndex: currentExtractIdx + 1,
                studentAnswerId: readingStudentAnswerId,
                practiceData: null,
              },
            });
            return;
          } else {
            console.log("Part B completed, moving to next part");
          }
        } else if (currentPart === "C") {
          const allQuestions = currentPartData?.questions || [];
          const uniqueExtractIds = [
            ...new Set(allQuestions.map((q) => q.readingTextId)),
          ];
          console.log(
            "Part C - uniqueExtractIds:",
            uniqueExtractIds.length,
            "currentExtractIdx:",
            currentExtractIdx
          );

          if (currentExtractIdx < uniqueExtractIds.length - 1) {
            console.log(
              "Navigating to next extract in Part C:",
              currentExtractIdx + 1
            );
            // Keep all answers preserved - never clear like listening module

            // Navigate directly to next extract WITHOUT showing instructions
            navigate(`${getBaseRoute()}/reading`, {
              state: {
                ...location.state,
                simulationStep: { ...simulationStep, readingPartIndex },
                currentExtractIndex: currentExtractIdx + 1,
                studentAnswerId: readingStudentAnswerId,
                practiceData: null,
              },
            });
            return;
          } else {
            console.log("Part C completed, moving to next part");
          }
        }

        // All extracts of this part done: move to next part (first extract)
        const nextReadingPartIndex = readingPartIndex + 1;
        console.log(
          "Checking if more reading parts available:",
          nextReadingPartIndex,
          "of",
          totalReadingParts
        );

        if (nextReadingPartIndex < totalReadingParts) {
          console.log("Moving to next reading part:", nextReadingPartIndex);
          setLocalReadingData(null);
          // Keep all answers preserved - never clear like listening module
          navigate(`${getBaseRoute()}/reading`, {
            state: {
              ...location.state,
              simulationStep: {
                ...simulationStep,
                readingPartIndex: nextReadingPartIndex,
              },
              currentExtractIndex: 0, // Reset for new part
              studentAnswerId: readingStudentAnswerId,
              practiceData: null,
            },
          });
          return;
        }

        // All reading parts done, move to writing/results
        console.log(
          "All reading parts completed! Checking selectedSections:",
          location.state?.selectedSections
        );
        console.log("isPracticeMode:", location.state?.isPracticeMode);

        const selectedSections = location.state?.selectedSections;
        const isPracticeMode = location.state?.isPracticeMode;

        // In simulation mode, automatically proceed to writing if it exists
        // In practice mode, check selectedSections
        const shouldNavigateToWriting = isPracticeMode
          ? selectedSections?.writing
          : location.state?.mockTest?.ieltsTest?.writingId ||
            location.state?.mockTest?.oetTest?.writingId; // Check if writing test exists in simulation

        if (shouldNavigateToWriting) {
          console.log("Writing section available, navigating to writing page");
          console.log(
            "Writing test ID:",
            isPracticeMode
              ? "practice mode"
              : location.state?.mockTest?.ieltsTest?.writingId ||
                  location.state?.mockTest?.oetTest?.writingId
          );

          // Update simulation step to indicate reading is completed
          const updatedSimulationStep = {
            ...simulationStep,
            module: "writing",
            readingCompleted: true,
            currentSection: "writing",
            previousSection: "reading",
            writingTaskIds: simulationStep.writingTaskIds || [],
            writingId:
              location.state?.mockTest?.ieltsTest?.writingId ||
              location.state?.mockTest?.oetTest?.writingId,
          };

          navigate(`${getBaseRoute()}/writing`, {
            state: {
              ...location.state,
              studentAnswerId: readingStudentAnswerId,
              simulationStep: updatedSimulationStep,
              practiceData: null,
              writingTestId:
                location.state?.mockTest?.ieltsTest?.writingId ||
                location.state?.mockTest?.oetTest?.writingId,
            },
          });
        } else {
          console.log(
            "No writing section available, checking final section logic"
          );
          console.log(
            "Reason:",
            isPracticeMode
              ? "Writing not selected in practice mode"
              : "No writing test ID in simulation"
          );

          // For practice mode, show results popup. For simulation, use existing navigation.
          if (isPracticeMode && isFinalSection()) {
            await showPracticeResults(studentAttemptId);
          } else {
            // Mark test attempt as completed before navigating to results
            if (studentAttemptId) {
              try {
                await updateTestAttempt({
                  studentAttemptId,
                  isCompleted: true,
                }).unwrap();
                console.log("Test attempt marked as completed");
              } catch (updateError) {
                console.error("Error marking test as completed:", updateError);
                // Continue to results even if update fails
              }
            }

            // Update simulation step to indicate reading is completed
            const updatedSimulationStep = {
              ...simulationStep,
              readingCompleted: true,
              testCompleted: true,
            };

            navigate(`${getBaseRoute()}/results`, {
              state: {
                ...location.state,
                studentAnswerId: readingStudentAnswerId,
                simulationStep: updatedSimulationStep,
              },
            });
          }
        }
        return;
      }

      // ----------------- OLD FALLBACK (optional) -----------------
      else {
        // Old fallback (can be removed if not needed)
        const parts = extractReadingParts(localReadingData);
        const partB = parts.find((p) => p.part === "B");
        const partC = parts.find((p) => p.part === "C");
        const totalPartBExtracts = partB?.oetReadingText?.length || 6;
        const totalPartCExtracts = partC?.oetReadingText?.length || 2;

        if (currentPart === "A") {
          // Transition from A to B - will show instructions via the useEffect
          setCurrentPart("B");
          setCurrentExtractIndex(0);
        } else if (currentPart === "B") {
          if (currentExtractIndex < totalPartBExtracts - 1) {
            // Move to next extract in Part B without instructions
            setCurrentExtractIndex(currentExtractIndex + 1);
            // Don't clear answers when moving within same part
          } else {
            // Transition from B to C - will show instructions via the useEffect
            setCurrentPart("C");
            setCurrentExtractIndex(0);
          }
        } else if (currentPart === "C") {
          if (currentExtractIndex < totalPartCExtracts - 1) {
            // Move to next extract in Part C without instructions
            setCurrentExtractIndex(currentExtractIndex + 1);
          } else {
            navigate(`${getBaseRoute()}/writing`, {
              state: {
                ...location.state,
                studentAnswerId: readingStudentAnswerId,
              },
            });
            return;
          }
        }
      }
    } catch (error) {
      console.error("Error in handleAutoNext:", error);
      dispatch(
        showNotification({
          message: "Error submitting answers. Please try again.",
          status: 0,
        })
      );
    }
  };

  // Handler for unattempted dialog confirmation
  const handleUnattemptedContinue = async () => {
    setPendingAutoNext(false);
    hideUnattemptedDialog();
    await proceedAutoNext();
  };

  // At the top of your component (just after state declarations):
  const simulationExtractIndex =
    isSimulationMode && typeof location.state?.currentExtractIndex === "number"
      ? location.state.currentExtractIndex
      : currentExtractIndex;

  console.log("=== EXTRACT INDEX DEBUG ===");
  console.log("isSimulationMode:", isSimulationMode);
  console.log(
    "location.state.currentExtractIndex:",
    location.state?.currentExtractIndex
  );
  console.log("currentExtractIndex (state):", currentExtractIndex);
  console.log("simulationExtractIndex (final):", simulationExtractIndex);
  console.log("=== END EXTRACT INDEX DEBUG ===");

  // Then, in your useCallback:
  const transformReadingData = useCallback(
    (apiData) => {
      const parts = extractReadingParts(apiData);

      const extractIdx = simulationExtractIndex; // always use this for index in all modes!

      console.log("transformReadingData called with:", {
        apiDataExists: !!apiData,
        currentPart,
        extractIdx,
        isSimulationMode,
        dataStructure: parts.map((p) => ({
          part: p.part || p.partName,
          partId: p._id,
          sectionId: p.sectionId,
          textsCount: p.oetReadingText?.length || (p.paragraph ? 1 : 0),
          questionsCount: p.questions?.length,
          questionTypes: p.questions?.map((q) => q.questionType),
          questionIds: p.questions?.map((q) => q.questionId),
        })),
      });

      if (!parts || !Array.isArray(parts) || parts.length === 0) {
        console.log(
          "transformReadingData returning null - no reading parts found"
        );
        return null;
      }

      // Check if this is IELTS data structure
      const isIeltsStructure = parts.some((p) => p.sectionId && p.paragraph);

      if (isIeltsStructure) {
        // Handle IELTS structure - find parts within the current section
        const currentSectionParts = parts.filter(
          (p) => p.sectionId === currentPart
        );

        if (!currentSectionParts || currentSectionParts.length === 0) {
          console.log("IELTS: No parts found for section:", currentPart);
          return null;
        }

        console.log("IELTS: Found section parts:", {
          totalPartsInSection: currentSectionParts.length,
          extractIdx,
          sectionId: currentPart,
          partNames: currentSectionParts.map((p) => p.partName),
        });

        // Helper function to transform part data
        const transformPartData = (partData) => {
          if (!partData)
            return {
              readingTexts: [],
              mcqQuestions: [],
              noteCompletionQuestions: [],
              sentenceCompletionQuestions: [],
            };

          return {
            heading: partData.partHeading || partData.sectionHeading,
            instructions: partData.instructions || partData.sectionInstructions,
            questionRange:
              partData.questionRange || partData.sectionQuestionRange,
            readingTexts: [
              {
                title: partData.textTitle || partData.partHeading,
                paragraphs: partData.paragraph ? [partData.paragraph] : [],
                imageUrl: null,
                tables: partData.table ? [partData.table] : [],
              },
            ],
            questions: (partData.questions || []).map((q) => {
              console.log("📝 Transforming question:", {
                _id: q._id,
                questionId: q.questionId,
                questionType: q.questionType,
                hasUnderscore: (q.question || "").includes("_")
              });
              return {
                ...q,
                question_id: q.questionId,
              };
            }),
            mcqQuestions: groupMcqMultipleQuestions(
              (partData.questions || [])
                .filter((q) =>
                  [
                    "mcq",
                    "mcq_single",
                    "mcq_multiple",
                  "matching_feature",
                  "matching_information",
                  "matching_sentence_endings",
                  "matching_headings",
                  "true_false_notgiven",
                  "tfng",
                  "yes_no_notgiven",
                  "map_labelling",
                ].includes(q.questionType)
              )
                .map((q) => ({
                  ...q,
                  question_id: q.questionId,
                }))
            ),
            noteCompletionQuestions: (partData.questions || [])
              .filter((q) =>
                [
                  "notes_completion",
                  "note_completion",
                  "notecompletion",
                  "summary_completion",
                  "table_completion",
                  "form_completion",
                  "flow_chart_completion",
                  "diagram_completion",
                ].includes(q.questionType)
              )
              .map((q) => ({
                ...q,
                question_id: q.questionId,
              })),
            sentenceCompletionQuestions: (partData.questions || [])
              .filter((q) =>
                [
                  "sentence_completion",
                  "sentencecompletion",
                  "short_question_answers",
                  "short_answer_questions",
                ].includes(q.questionType)
              )
              .map((q) => ({
                ...q,
                question_id: q.questionId,
              })),
          };
        };

        // Determine if this is a single-part section (like Section 3) or multi-part section (like Sections 1 and 2)
        // If there's only one part OR if all questions are in the first part, treat as single-part
        const isSinglePartSection =
          currentSectionParts.length === 1 ||
          (currentSectionParts.length > 1 &&
            currentSectionParts[0].questions &&
            currentSectionParts[0].questions.length > 1 &&
            currentSectionParts
              .slice(1)
              .every((part) => !part.questions || part.questions.length === 0));

        console.log("IELTS Section Analysis:", {
          sectionId: currentPart,
          totalParts: currentSectionParts.length,
          isSinglePartSection,
          partsWithQuestions: currentSectionParts.map((p) => ({
            partName: p.partName,
            questionCount: p.questions?.length || 0,
            questionIds: p.questions?.map((q) => q.questionId) || [],
          })),
        });

        if (isSinglePartSection) {
          // Single-part section: combine all questions from all parts (like Section 3)
          const allQuestionsInSection = [];
          currentSectionParts.forEach((part) => {
            if (part.questions) {
              allQuestionsInSection.push(...part.questions);
            }
          });

          // Sort all questions by questionId
          allQuestionsInSection.sort((a, b) => {
            const aId = a.questionId || a._id || 0;
            const bId = b.questionId || b._id || 0;
            return aId - bId;
          });

          // Get the current part for reading text (based on extractIdx)
          const currentPartForText =
            currentSectionParts[extractIdx] || currentSectionParts[0];

          // Create a combined part data that has the text from current part but ALL questions
          const combinedPartData = {
            heading:
              currentPartForText.partHeading ||
              currentPartForText.sectionHeading,
            instructions:
              currentPartForText.instructions ||
              currentPartForText.sectionInstructions,
            questionRange:
              currentPartForText.questionRange ||
              currentPartForText.sectionQuestionRange,
            readingTexts: [
              {
                title:
                  currentPartForText.textTitle ||
                  currentPartForText.partHeading,
                paragraphs: currentPartForText.paragraph
                  ? [currentPartForText.paragraph]
                  : [],
                imageUrl: null,
                tables: currentPartForText.table
                  ? [currentPartForText.table]
                  : [],
              },
            ],
            questions: allQuestionsInSection.map((q) => ({
                ...q,
                question_id: q.questionId,
              })),
            mcqQuestions: groupMcqMultipleQuestions(
              allQuestionsInSection
                .filter((q) =>
                  [
                    "mcq",
                    "mcq_single",
                    "mcq_multiple",
                    "matching_feature",
                    "matching_information",
                    "matching_sentence_endings",
                    "matching_headings",
                    "true_false_notgiven",
                    "tfng",
                    "yes_no_notgiven",
                    "map_labelling",
                  ].includes(q.questionType)
                )
                .map((q) => ({
                  ...q,
                  question_id: q.questionId,
                }))
            ),
            sentenceCompletionQuestions: allQuestionsInSection
              .filter((q) =>
                [
                  "sentence_completion",
                  "sentencecompletion",
                  "short_question_answers",
                  "short_answer_questions",
                ].includes(q.questionType)
              )
              .map((q) => ({
                ...q,
                question_id: q.questionId,
              })),
            noteCompletionQuestions: allQuestionsInSection
              .filter((q) =>
                [
                  "notes_completion",
                  "note_completion",
                  "notecompletion",
                  "summary_completion",
                  "table_completion",
                  "form_completion",
                  "flow_chart_completion",
                  "diagram_completion",
                ].includes(q.questionType)
              )
              .map((q) => ({
                ...q,
                question_id: q.questionId,
              })),
          };

          // Return all data in partA since that's what ReadingSection expects for single-part IELTS sections
          return {
            partA: combinedPartData,
            partB: {
              readingTexts: [],
              questions: [],
              mcqQuestions: [],
              sentenceCompletionQuestions: [],
              noteCompletionQuestions: [],
            },
            partC: {
              readingTexts: [],
              questions: [],
              mcqQuestions: [],
              sentenceCompletionQuestions: [],
              noteCompletionQuestions: [],
            },
          };
        } else {
          // Multi-part section: show only the current part (like Sections 1 and 2)
          const currentPartData =
            currentSectionParts[extractIdx] || currentSectionParts[0];
          const transformedCurrentPart = transformPartData(currentPartData);

          // Return the structure based on extractIdx (which part we're showing)
          const result = {
            partA:
              extractIdx === 0
                ? transformedCurrentPart
                : {
                    readingTexts: [],
                    questions: [],
                    mcqQuestions: [],
                    sentenceCompletionQuestions: [],
                    noteCompletionQuestions: [],
                  },
            partB:
              extractIdx === 1
                ? transformedCurrentPart
                : {
                    readingTexts: [],
                    questions: [],
                    mcqQuestions: [],
                    sentenceCompletionQuestions: [],
                    noteCompletionQuestions: [],
                  },
            partC:
              extractIdx === 2
                ? transformedCurrentPart
                : {
                    readingTexts: [],
                    questions: [],
                    mcqQuestions: [],
                    sentenceCompletionQuestions: [],
                    noteCompletionQuestions: [],
                  },
          };

          return result;
        }
      }

      // ---------- PART A ----------
      if (currentPart === "A") {
        const partA = parts.find((p) => p.part === "A");
        if (!partA) return null;

        // Handle both old OET structure (oetReadingText array) and new simple structure (direct paragraph)
        const readingTexts = partA.oetReadingText
          ? partA.oetReadingText.map((text) => ({
              title: text.title,
              paragraphs: text.paragraph ? [text.paragraph] : [],
              imageUrl: text.imageUrl || null,
              tables: text.tables || [],
            }))
          : partA.paragraph
          ? [
              {
                title: partA.textTitle || partA.partHeading,
                paragraphs: [partA.paragraph],
                imageUrl: null,
                tables: [],
              },
            ]
          : [];

        return {
          partA: {
            heading: partA.heading || partA.partHeading,
            instructions: partA.instructions,
            questionRange: partA.questionRange,
            readingTexts,
            mcqQuestions:
              partA.questions
                ?.filter((q) => q.questionType === "mcq")
                .map((q) => ({
                  ...q,
                  question_id: q.questionId,
                })) || [],
            sentenceCompletionQuestions:
              partA.questions
                ?.filter((q) => q.questionType === "sentencecompletion")
                .map((q) => ({
                  ...q,
                  question_id: q.questionId,
                })) || [],
            noteCompletionQuestions:
              partA.questions
                ?.filter((q) => q.questionType === "notecompletion")
                .map((q) => ({
                  ...q,
                  question_id: q.questionId,
                })) || [],
          },
          partB: { readingTexts: [] },
          partC: { readingTexts: [] },
        };
      }

      // ---------- PART B ----------
      if (currentPart === "B") {
        const partB = parts.find((p) => p.part === "B");
        if (!partB) return null;

        // Check if this is the new simple format (single paragraph) or OET format (oetReadingText array)
        if (partB.paragraph && !partB.oetReadingText) {
          // New simple format: single paragraph for the entire part
          return {
            partA: { readingTexts: [] },
            partB: {
              heading: partB.heading || partB.partHeading,
              instructions: partB.instructions,
              questionRange: partB.questionRange,
              readingTexts: [
                {
                  title: partB.textTitle || partB.partHeading,
                  paragraphs: [partB.paragraph],
                  imageUrl: null,
                  tables: [],
                },
              ],
              mcqQuestions:
                partB.questions
                  ?.filter((q) => q.questionType === "mcq")
                  .map((q) => ({
                    ...q,
                    question_id: q.questionId,
                  })) || [],
            },
            partC: { readingTexts: [] },
          };
        }

        // OET format: multiple extracts
        const allExtracts = partB.oetReadingText || [];
        const allQuestions = partB.questions || [];

        // In practice mode, we need to get the correct question for the current extract
        // The extractIdx represents which question we're currently showing from the selected questions
        let currentQuestion = null;
        let currentExtract = null;

        if (isPracticeMode) {
          // In practice mode, the questions are already filtered to only include selected ones
          // So we can directly use extractIdx to get the current question
          currentQuestion = allQuestions[extractIdx];

          // Find the extract that matches this question's readingTextId
          if (currentQuestion) {
            currentExtract = allExtracts.find(
              (extract) => extract._id === currentQuestion.readingTextId
            );
          }

          // Fallback: if no match found, use traditional mapping
          if (!currentExtract) {
            currentExtract = allExtracts[extractIdx];
          }
        } else {
          // In simulation mode, use the original logic
          currentExtract = allExtracts[extractIdx];
          currentQuestion =
            allQuestions.find((q) => q.readingTextId === currentExtract?._id) ||
            allQuestions[extractIdx]; // fallback
        }

        console.log("=== PART B TRANSFORM DEBUG ===");
        console.log("isPracticeMode:", isPracticeMode);
        console.log("extractIdx:", extractIdx);
        console.log("allExtracts.length:", allExtracts.length);
        console.log("allQuestions.length:", allQuestions.length);
        console.log(
          "currentExtract:",
          currentExtract?.title,
          currentExtract?._id
        );
        console.log(
          "currentQuestion:",
          currentQuestion?.questionId,
          currentQuestion?.readingTextId
        );
        console.log("=== END PART B TRANSFORM DEBUG ===");

        return {
          partA: { readingTexts: [] },
          partB: {
            heading: partB.heading,
            readingTexts: [
              {
                title: currentExtract?.title,
                paragraphs: currentExtract?.paragraph
                  ? [currentExtract.paragraph]
                  : [],
                imageUrl: currentExtract?.imageUrl || null,
                tables: currentExtract?.tables || [],
              },
            ],
            mcqQuestions: currentQuestion
              ? [
                  {
                    ...currentQuestion,
                    question_id: currentQuestion.questionId,
                  },
                ]
              : [],
          },
          partC: { readingTexts: [] },
        };
      }

      // ---------- PART C ----------
      if (currentPart === "C") {
        const partC = parts.find((p) => p.part === "C");
        if (!partC) return null;

        // Check if this is the new simple format (single paragraph) or OET format (oetReadingText array)
        if (partC.paragraph && !partC.oetReadingText) {
          // New simple format: single paragraph for the entire part
          return {
            partA: { readingTexts: [] },
            partB: { readingTexts: [] },
            partC: {
              heading: partC.heading || partC.partHeading,
              instructions: partC.instructions,
              questionRange: partC.questionRange,
              readingTexts: [
                {
                  title: partC.textTitle || partC.partHeading,
                  paragraphs: [partC.paragraph],
                  imageUrl: null,
                  tables: [],
                },
              ],
              mcqQuestions:
                partC.questions
                  ?.filter((q) => q.questionType === "mcq")
                  .map((q) => ({
                    ...q,
                    question_id: q.questionId,
                  })) || [],
            },
          };
        }

        // OET format: multiple extracts
        const allExtracts = partC.oetReadingText || [];
        const allQuestions = partC.questions || [];

        let currentExtract = null;
        let extractQuestions = [];

        if (isPracticeMode) {
          // In practice mode, questions are already filtered to selected ones
          // Group questions by their readingTextId to find extracts
          const questionsByExtract = {};
          allQuestions.forEach((q) => {
            const extractId = q.readingTextId;
            if (!questionsByExtract[extractId]) {
              questionsByExtract[extractId] = [];
            }
            questionsByExtract[extractId].push(q);
          });

          // Get the unique extract IDs in the order they appear in the selected questions
          const uniqueExtractIds = Object.keys(questionsByExtract);

          // Use extractIdx to determine which extract we're showing
          const currentExtractId = uniqueExtractIds[extractIdx];

          if (currentExtractId) {
            currentExtract = allExtracts.find(
              (extract) => extract._id === currentExtractId
            );
            extractQuestions = questionsByExtract[currentExtractId] || [];
          }

          console.log("=== PART C PRACTICE MODE DEBUG ===");
          console.log("extractIdx:", extractIdx);
          console.log("uniqueExtractIds:", uniqueExtractIds);
          console.log("currentExtractId:", currentExtractId);
          console.log(
            "extractQuestions:",
            extractQuestions.map((q) => q.questionId)
          );
          console.log("=== END PART C PRACTICE MODE DEBUG ===");
        } else {
          // Simulation mode: use the original logic
          const uniqueExtractIds = [
            ...new Set(allQuestions.map((q) => q.readingTextId)),
          ];

          const currentExtractId = uniqueExtractIds[extractIdx];
          currentExtract = allExtracts.find(
            (extract) => extract._id === currentExtractId
          );

          if (currentExtractId) {
            extractQuestions = allQuestions.filter(
              (q) => q.readingTextId === currentExtractId
            );
          }
        }

        // Fallback: if no questions found via readingTextId, use the traditional slicing method
        if (extractQuestions.length === 0) {
          const extractStart = extractIdx * 8;
          const extractEnd = extractStart + 8;
          extractQuestions = allQuestions.slice(extractStart, extractEnd);
          if (extractQuestions.length === 0 && allQuestions.length > 0) {
            extractQuestions = allQuestions;
          }
        }
        if (extractQuestions.length === 0) {
          const extractStart = extractIdx * 8;
          const extractEnd = extractStart + 8;
          extractQuestions = allQuestions.slice(extractStart, extractEnd);
          if (extractQuestions.length === 0 && allQuestions.length > 0) {
            extractQuestions = allQuestions;
          }
        }

        return {
          partA: { readingTexts: [] },
          partB: { readingTexts: [] },
          partC: {
            heading: partC.heading,
            readingTexts: [
              {
                title: currentExtract?.title,
                paragraphs: currentExtract?.paragraph
                  ? [currentExtract.paragraph]
                  : [],
                imageUrl: currentExtract?.imageUrl || null,
                tables: currentExtract?.tables || [],
              },
            ],
            mcqQuestions: extractQuestions.map((q) => ({
              ...q,
              question_id: q.questionId,
            })),
          },
        };
      }

      console.log(
        "transformReadingData returning null - currentPart didn't match any case:",
        currentPart
      );
      return null;
    },
    [
      currentPart,
      simulationExtractIndex, // Only depend on this, not currentExtractIndex!
      isSimulationMode,
      isPracticeMode,
    ]
  );

  const readingData = useMemo(() => {
    // Use practice data from navigation state if available, otherwise use localReadingData
    const dataToTransform = localReadingData || location.state?.practiceData;

    if (!dataToTransform) {
      console.log("ReadingData memo: No data to transform");
      return null;
    }

    console.log("=== READING DATA TRANSFORMATION DEBUG ===");
    console.log("dataToTransform exists:", !!dataToTransform);
    console.log("currentPart:", currentPart);
    console.log("isSimulationMode:", isSimulationMode);
    console.log("simulationStep:", location.state?.simulationStep);
    console.log(
      "localReadingData structure:",
      extractReadingParts(dataToTransform)
    );
    console.log("=== END DEBUG ===");

    console.log(
      "Memo: transforming reading data with currentPart:",
      currentPart,
      "dataSource:",
      localReadingData ? "localReadingData" : "practiceDataFromState"
    );
    const result = transformReadingData(dataToTransform);
    console.log("Transformation result:", result);
    return result;
  }, [
    localReadingData,
    location.state?.practiceData,
    currentPart,
    transformReadingData,
  ]);

  const totalExtracts = (() => {
    const parts = extractReadingParts(localReadingData);

    // Check if this is IELTS structure
    const isIeltsStructure = parts.some((p) => p.sectionId && p.paragraph);

    if (isIeltsStructure) {
      // For IELTS, count the number of parts within the current section
      const currentSectionParts = parts.filter(
        (p) => p.sectionId === currentPart
      );
      console.log("IELTS totalExtracts calculation:", {
        currentPart,
        totalPartsInSection: currentSectionParts.length,
        partNames: currentSectionParts.map((p) => p.partName),
      });
      return currentSectionParts.length || 1;
    }

    // Handle OET structure
    const currentPartData = parts.find((p) => p.part === currentPart);

    if (currentPart === "A") {
      return 1;
    } else if (currentPart === "B") {
      // For both modes: number of extracts = number of questions (1:1 mapping)
      return currentPartData?.questions?.length || 1;
    } else if (currentPart === "C") {
      // For both modes: number of unique extracts based on readingTextId
      const allQuestions = currentPartData?.questions || [];
      const uniqueExtractIds = [
        ...new Set(allQuestions.map((q) => q.readingTextId)),
      ];
      return uniqueExtractIds.length || 1;
    }

    return 1;
  })();

  // Check if we should show loader
  const practiceDataFromState = location.state?.practiceData;
  const hasAnyData = localReadingData || location.state?.practiceData;
  const shouldShowLoader = () => {
    // Don't show loader when instructions are visible - we want to show instructions on blank/blurred screen
    if (instructionState.showInstructions) {
      return false;
    }

    // After instructions close, only show loader if we're loading API data
    const apiLoading = isPracticeMode ? practiceLoading : isLoading;

    // If we don't have data yet and instructions are closed, show loader
    const noDataYet = !localReadingData && !location.state?.practiceData;

    // For simulation mode: only show loader if API is currently loading, not just missing localReadingData
    const simulationNeedsData =
      isSimulationMode &&
      location.state?.simulationStep &&
      !hasAnyData &&
      apiLoading;

    // Also show loader if we have raw data but readingData is still null (transformation in progress)
    const transformationInProgress =
      hasAnyData && !readingData && instructionsViewed && !apiLoading;

    return (
      (apiLoading ||
        noDataYet ||
        simulationNeedsData ||
        transformationInProgress) &&
      instructionsViewed
    );
  };

  if (shouldShowLoader()) {
    console.log(
      "ReadingPage - Showing loader. currentLoading:",
      currentLoading,
      "localReadingData:",
      !!localReadingData,
      "isPracticeMode:",
      isPracticeMode,
      "practiceTestData:",
      !!practiceTestData,
      "testData:",
      !!testData,
      "practiceDataFromState:",
      !!practiceDataFromState
    );

    return (
      <>
        {/* Instruction System Wrapper - must render even during loading */}
        <InstructionWrapper
          instructionState={instructionState}
          onInstructionNext={async () => {
            // Acknowledge instruction when user clicks Next
            if (studentAttemptId) {
              try {
                await acknowledgeInstruction({
                  studentAttemptId,
                  module: "reading",
                }).unwrap();
                setInstructionsAcknowledged(true);
                setInstructionsViewed(true);
              } catch (error) {
                console.error("Failed to acknowledge instruction:", error);
              }
            }
            hideInstructionModal();
          }}
          onInstructionClose={async () => {
            // Acknowledge instruction when modal closes
            if (studentAttemptId) {
              try {
                await acknowledgeInstruction({
                  studentAttemptId,
                  module: "reading",
                }).unwrap();
                setInstructionsAcknowledged(true);
                setInstructionsViewed(true);
              } catch (error) {
                console.error("Failed to acknowledge instruction:", error);
              }
            }
            hideInstructionModal();
          }}
          onConfirmationConfirm={() => {
            hideConfirmationDialog();
            hideUnattemptedDialog();
          }}
          onConfirmationClose={hideConfirmationDialog}
          onUnattemptedContinue={handleUnattemptedContinue}
          onUnattemptedClose={() => {
            hideUnattemptedDialog();
          }}
          onTimeExpiryContinue={hideTimeExpiryDialog}
          timeRemaining={null}
          unattemptedCount={unattemptedCount}
          nextSection="writing"
          instructionData={instructionData?.data}
          instructionLoading={instructionLoading}
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <CircularProgress size={60} thickness={5} />
          <Typography sx={{ ml: 2 }}>Loading reading data...</Typography>
        </Box>
      </>
    );
  }

  if (currentError) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography color="error">
          Error loading reading test: {currentError.message}
        </Typography>
      </Box>
    );
  }

  // Debug logging
  console.log("ReadingPage - Raw API data:", localReadingData);
  console.log(
    "ReadingPage - Practice data from state:",
    location.state?.practiceData
  );
  console.log("ReadingPage - Transformed data:", readingData);
  console.log("ReadingPage - Current part state:", currentPart);
  console.log("ReadingPage - Practice mode:", isPracticeMode);
  console.log(
    "ReadingPage - Current reading part index:",
    location.state?.currentReadingPartIndex
  );
  console.log(
    "ReadingPage - localReadingData structure:",
    extractReadingParts(localReadingData)
  );

  if (!readingData && !currentLoading && hasAnyData) {
    console.log("=== NO READING DATA AVAILABLE DEBUG ===");
    console.log("ReadingPage - No reading data available after transformation");
    console.log("localReadingData:", localReadingData);
    console.log("practiceDataFromState:", location.state?.practiceData);
    console.log("currentPart:", currentPart);
    console.log("isSimulationMode:", isSimulationMode);
    console.log("simulationStep:", location.state?.simulationStep);
    console.log("extractReadingParts result:", extractReadingParts(hasAnyData));
    console.log(
      "transformReadingData result:",
      transformReadingData(hasAnyData)
    );
    console.log("currentLoading:", currentLoading);
    console.log("instructionsViewed:", instructionsViewed);
    console.log("=== END DEBUG ===");

    // Instead of showing error, let's try to auto-fix the currentPart
    const parts = extractReadingParts(hasAnyData);
    if (parts && parts.length > 0 && !currentPart) {
      console.log(
        "Auto-fixing currentPart to first available part:",
        parts[0].part || parts[0]._id
      );
      setCurrentPart(parts[0].part || parts[0]._id || "A");
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <CircularProgress size={60} thickness={5} />
          <Typography sx={{ ml: 2 }}>Initializing reading data...</Typography>
        </Box>
      );
    }

    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Box textAlign="center">
          <Typography>No reading data available</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Debug: currentPart={currentPart}, hasData={!!hasAnyData},
            partsFound={parts?.length || 0}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Final debug before rendering ReadingSection
  console.log("=== READING FINAL RENDER DEBUG ===");
  console.log("About to render ReadingSection with:");
  console.log("- currentPart:", currentPart);
  console.log("- currentExtractIndex:", currentExtractIndex);
  console.log("- readingData available:", !!readingData);
  console.log("- isPracticeMode:", isPracticeMode);
  console.log("=== END READING FINAL RENDER DEBUG ===");

  return (
    <>
      {/* Instruction System Wrapper */}
      <InstructionWrapper
        instructionState={instructionState}
        onInstructionNext={async () => {
          // Acknowledge instruction when user clicks Next
          if (studentAttemptId) {
            try {
              await acknowledgeInstruction({
                studentAttemptId,
                module: "reading",
              }).unwrap();
              setInstructionsAcknowledged(true);
              setInstructionsViewed(true);
            } catch (error) {
              console.error("Failed to acknowledge instruction:", error);
            }
          }
          hideInstructionModal();
        }}
        onInstructionClose={async () => {
          // Acknowledge instruction when modal closes
          if (studentAttemptId) {
            try {
              await acknowledgeInstruction({
                studentAttemptId,
                module: "reading",
              }).unwrap();
              setInstructionsAcknowledged(true);
              setInstructionsViewed(true);
            } catch (error) {
              console.error("Failed to acknowledge instruction:", error);
            }
          }
          hideInstructionModal();
        }}
        onConfirmationConfirm={() => {
          hideConfirmationDialog();
          hideUnattemptedDialog();
        }}
        onConfirmationClose={hideConfirmationDialog}
        onUnattemptedContinue={handleUnattemptedContinue}
        onUnattemptedClose={() => {
          hideUnattemptedDialog();
        }}
        onTimeExpiryContinue={hideTimeExpiryDialog}
        timeRemaining={null}
        unattemptedCount={unattemptedCount}
        nextSection="writing"
        instructionData={instructionData?.data}
        instructionLoading={instructionLoading}
      />
      <ReadingSection
        key={`reading-${currentPart}-${
          location.state?.currentReadingPartIndex || 0
        }-${simulationExtractIndex}`}
        readingData={readingData}
        rawReadingData={localReadingData} // Pass raw data for navigation
        screen={0}
        instrOpen={
          instructionState.showInstructions &&
          instructionState.currentSection === "reading"
        }
        answers={answers}
        onAnswerChange={handleAnswerChange}
        onAutoNext={handleAutoNext}
        setCurrentPart={setCurrentPart}
        setCurrentExtractIndex={setCurrentExtractIndex}
        setAnswers={setAnswers}
        onJumpToExtract={handleJumpToExtract}
        timer="no-limit"
        mode={isPracticeMode ? "practice" : "simulation"}
        currentPart={currentPart}
        currentExtractIndex={simulationExtractIndex}
        totalExtracts={totalExtracts}
        instructionsViewed={instructionsViewed}
        startTime={
          practiceTimerStartTime || localReadingData?.data?.startTime || null
        }
        endTime={
          practiceTimerEndTime || localReadingData?.data?.endTime || null
        }
        testId={testId}
        bundleId={bundleId}
        // Notes props - persistent across parts/extracts
        notes={notes}
        onAddNote={handleAddNote}
        notesModalOpen={notesModalOpen}
        setNotesModalOpen={setNotesModalOpen}
        onBack={async (prevExtractIndex, part) => {
          console.log("=== READING ONBACK CALLED ===");
          console.log("prevExtractIndex:", prevExtractIndex);
          console.log("part:", part);
          console.log("currentPart:", currentPart);
          console.log("isPracticeMode:", location.state?.isPracticeMode);
          console.log("isSimulationMode:", !location.state?.isPracticeMode);

          // Special case: Going back from Part C extract 1 to Part B
          if (currentPart === "C" && prevExtractIndex === -1) {
            const mode = isSimulationMode ? "SIMULATION" : "PRACTICE";
            console.log(`[${mode}] Going back from Part C to Part B`);

            console.log(
              `[${mode}] Current answers before navigation:`,
              answers
            );

            const studentAttemptId = location.state?.studentAttemptId;
            const isPracticeMode = location.state?.isPracticeMode;

            if (isPracticeMode) {
              // Practice Mode: Need to fetch Part B data
              try {
                const readingPartsOrder =
                  location.state?.readingPartsOrder || [];
                const readingQuestionsOrder =
                  location.state?.readingQuestionsOrder || [];
                const currentReadingPartIndex =
                  location.state?.currentReadingPartIndex || 0;

                console.log("readingPartsOrder:", readingPartsOrder);
                console.log(
                  "currentReadingPartIndex:",
                  currentReadingPartIndex
                );

                // For practice mode, we need to find the Part B index
                // Since we're currently in Part C, Part B should be before it
                // We know that if we're in Part C, Part B must be at currentReadingPartIndex - 1
                let partBIndex = currentReadingPartIndex - 1;

                console.log(
                  "Calculated Part B index based on current Part C index:",
                  partBIndex
                );
                console.log(
                  "readingPartsOrder.length:",
                  readingPartsOrder.length
                );

                if (partBIndex >= 0 && partBIndex < readingPartsOrder.length) {
                  console.log("Fetching Part B data for practice mode");

                  const partBData = await triggerGetPracticeData({
                    studentAttemptId,
                    currentSection: "reading",
                    readingId: location.state?.readingTestId,
                    readingSectionId: readingPartsOrder[partBIndex],
                    readingQuestionIds: readingQuestionsOrder,
                  }).unwrap();

                  console.log(
                    "Part B data fetched successfully, navigating..."
                  );

                  // Navigate back to Part B with the fetched data
                  navigate(`${getBaseRoute()}/reading`, {
                    state: {
                      ...location.state,
                      currentReadingPartIndex: partBIndex,
                      practiceData: partBData,
                      // Don't reset currentExtractIndex - let it be restored from saved state
                    },
                  });
                  return;
                } else {
                  console.error(
                    "Invalid Part B index:",
                    partBIndex,
                    "readingPartsOrder.length:",
                    readingPartsOrder.length
                  );
                  dispatch(
                    showNotification({
                      message:
                        "Could not navigate to Part B. Please try again.",
                      status: 0,
                    })
                  );
                  return;
                }
              } catch (error) {
                console.error("Error fetching Part B data:", error);
                dispatch(
                  showNotification({
                    message: "Error loading Part B. Please try again.",
                    status: 0,
                  })
                );
                return;
              }
            } else {
              // Simulation Mode: Navigate back to Part B
              const simulationStep = location.state?.simulationStep;
              const readingPartIds = simulationStep?.readingPartIds || [];

              console.log("simulationStep:", simulationStep);
              console.log("readingPartIds:", readingPartIds);
              console.log("currentPart:", currentPart);

              // Determine the correct current reading part index based on the actual current part
              // Standard OET Reading order: Part A (index 0), Part B (index 1), Part C (index 2)
              let currentPartIndex = -1;

              // Map current part letter to index
              if (currentPart === "A") {
                currentPartIndex = 0;
              } else if (currentPart === "B") {
                currentPartIndex = 1;
              } else if (currentPart === "C") {
                currentPartIndex = 2;
              }

              console.log(
                "Determined current part index based on currentPart:",
                currentPartIndex
              );

              // Calculate Part B index (should be 1 for standard A, B, C order)
              let partBIndex = currentPartIndex - 1;

              console.log("Calculated Part B simulation index:", partBIndex);

              if (partBIndex >= 0 && partBIndex < readingPartIds.length) {
                console.log(
                  "[SIMULATION] Navigating back to Part B in simulation mode"
                );

                try {
                  // For simulation mode, use getPracticeData API like practice mode
                  const readingQuestionsOrder =
                    location.state?.readingQuestionsOrder || [];

                  console.log(
                    "[SIMULATION] Fetching Part B data with readingQuestionsOrder:",
                    readingQuestionsOrder
                  );

                  const partBData = await triggerGetPracticeData({
                    studentAttemptId,
                    currentSection: "reading",
                    readingId: location.state?.readingTestId,
                    readingSectionId: readingPartIds[partBIndex],
                    readingQuestionIds: readingQuestionsOrder,
                  }).unwrap();

                  console.log(
                    "[SIMULATION] Part B data fetched successfully, navigating..."
                  );

                  navigate(`${getBaseRoute()}/reading`, {
                    state: {
                      ...location.state,
                      simulationStep: {
                        ...simulationStep,
                        readingPartIndex: partBIndex,
                      },
                      // Don't reset currentExtractIndex - let it be restored from saved state
                      practiceData: partBData, // Use practiceData like practice mode
                    },
                  });
                  return;
                } catch (error) {
                  console.error(
                    "Error fetching Part B data for simulation:",
                    error
                  );
                  dispatch(
                    showNotification({
                      message: "Error loading Part B. Please try again.",
                      status: 0,
                    })
                  );
                  return;
                }
              } else {
                console.error(
                  "Invalid Part B simulation index:",
                  partBIndex,
                  "readingPartIds.length:",
                  readingPartIds.length
                );
                dispatch(
                  showNotification({
                    message: "Could not navigate to Part B. Please try again.",
                    status: 0,
                  })
                );
                return;
              }
            }
          }

          // Regular back navigation within the same part
          if (
            location.state?.isPracticeMode &&
            (part === "B" || part === "C") &&
            typeof prevExtractIndex === "number" &&
            prevExtractIndex >= 0
          ) {
            // Practice mode: local index - don't clear answers when going back within same part
            console.log(
              `Going back to extract ${prevExtractIndex} within Part ${part}`
            );
            setCurrentExtractIndex(prevExtractIndex);
            // Don't clear answers when navigating within the same part
            return;
          }

          // --- Simulation Mode: must use navigation ---
          if (
            !location.state?.isPracticeMode &&
            (part === "B" || part === "C") &&
            typeof prevExtractIndex === "number" &&
            prevExtractIndex >= 0
          ) {
            // Always navigate with correct simulationStep and index!
            navigate(`${getBaseRoute()}/reading`, {
              state: {
                ...location.state,
                currentExtractIndex: prevExtractIndex,
                // If you use simulationStep, keep it as-is:
                simulationStep: location.state?.simulationStep,
                // Optionally clear answers, or keep as needed:
                practiceData: null,
              },
            });
            return;
          }

          // Default: go back to previous section (existing behavior)
          const sectionOrder = location.state?.sectionOrder || [];
          const currentSectionIndex = location.state?.currentSectionIndex || 0;
          if (currentSectionIndex > 0) {
            const previousSection = sectionOrder[currentSectionIndex - 1];
            navigate(`${getBaseRoute()}/${previousSection}`, {
              state: {
                ...location.state,
                currentSectionIndex: currentSectionIndex - 1,
              },
            });
          }
        }}
        isLastQuestion={
          location.state?.isPracticeMode &&
          (() => {
            // In practice mode, check if this is the very last question

            // First, check if reading is the last SELECTED section
            const selectedSections = location.state?.selectedSections || {};

            // Check if there are any selected sections after reading
            const hasWritingSelected = selectedSections.writing === true;
            const hasSpeakingSelected = selectedSections.speaking === true;
            const hasAnySelectedSectionsAfterReading =
              hasWritingSelected || hasSpeakingSelected;

            console.log("ReadingPage isLastQuestion debug:", {
              isPracticeMode: location.state?.isPracticeMode,
              selectedSections,
              hasWritingSelected,
              hasSpeakingSelected,
              hasAnySelectedSectionsAfterReading,
              readingPartsOrder: location.state?.readingPartsOrder,
              currentReadingPartIndex: location.state?.currentReadingPartIndex,
              currentPart,
            });

            // If there are selected sections after reading, this is not the last question
            if (hasAnySelectedSectionsAfterReading) return false;

            // Now check if this is the last question within the reading section
            const readingPartsOrder = location.state?.readingPartsOrder || [];
            // In practice mode, use the state variable that gets updated; otherwise use location.state
            const currentReadingPartIndex = isPracticeMode
              ? currentSectionIndex
              : location.state?.currentReadingPartIndex || 0;

            // Check if we're on the last part of reading
            const isLastReadingPart =
              currentReadingPartIndex === readingPartsOrder.length - 1;
            if (!isLastReadingPart) return false;

            // Now check if we're on the last extract/question within this part
            const parts = extractReadingParts(localReadingData);

            // Check if this is IELTS structure
            const isIeltsStructure = parts.some(
              (p) => p.sectionId && p.paragraph
            );

            if (isIeltsStructure) {
              // For IELTS, check if we're on the last reading section AND last extract within it
              const currentSectionParts = parts.filter(
                (p) => p.sectionId === currentPart
              );
              const isLastExtractInSection =
                currentExtractIndex >= currentSectionParts.length - 1;
              console.log(
                "ReadingPage: IELTS structure detected - checking if last section",
                {
                  currentReadingPartIndex,
                  readingPartsOrderLength: readingPartsOrder.length,
                  isLastPart: isLastReadingPart,
                  currentExtractIndex,
                  totalPartsInSection: currentSectionParts.length,
                  isLastExtractInSection,
                }
              );
              return isLastReadingPart && isLastExtractInSection;
            }

            // OET logic
            const currentPartData = parts.find((p) => p.part === currentPart);

            if (!currentPartData) return false;

            if (currentPart === "A") {
              // Part A has only one extract, so if we're on last reading part and Part A, it's the last question
              console.log("ReadingPage: Part A detected as last question");
              return true;
            } else if (currentPart === "B") {
              // Part B: each extract has one question, check if we're on the last extract
              const totalQuestions = currentPartData?.questions?.length || 1;
              const isLastExtract =
                simulationExtractIndex >= totalQuestions - 1;
              console.log("ReadingPage: Part B last extract check:", {
                simulationExtractIndex,
                totalQuestions,
                isLastExtract,
              });
              return isLastExtract;
            } else if (currentPart === "C") {
              // Part C: multiple extracts, check if we're on the last extract
              const allQuestions = currentPartData?.questions || [];
              const uniqueExtractIds = [
                ...new Set(allQuestions.map((q) => q.readingTextId)),
              ];
              return simulationExtractIndex >= uniqueExtractIds.length - 1;
            }

            return false;
          })()
        }
        isLastModuleInSimulation={
          !location.state?.isPracticeMode &&
          (() => {
            // In simulation mode, check if reading is the last module in the package
            const mockTest = location.state?.mockTest;
            if (!mockTest) return false;

            // Check if there are any modules after reading (writing only, speaking is not in simulation)
            const hasWriting = !!(
              mockTest?.ieltsTest?.writingId || mockTest?.oetTest?.writingId
            );

            console.log("ReadingPage isLastModuleInSimulation debug:", {
              hasWriting,
              ieltsWritingId: mockTest?.ieltsTest?.writingId,
              oetWritingId: mockTest?.oetTest?.writingId,
            });

            // If no writing after reading, this is the last module (speaking not included in simulation)
            return !hasWriting;
          })()
        }
        isLastQuestionInSimulation={
          !location.state?.isPracticeMode &&
          (() => {
            // In simulation mode, check if this is the last question/extract in reading
            const parts = extractReadingParts(localReadingData);

            // Check if this is IELTS structure
            const isIeltsStructure = parts.some(
              (p) => p.sectionId && p.paragraph
            );

            if (isIeltsStructure) {
              // For IELTS in simulation, finish only on the last extract
              // of the last section.
              const readingSectionIds =
                location.state?.simulationStep?.readingSectionIds || [];
              const currentReadingSectionIndex =
                location.state?.simulationStep?.readingPartIndex ?? 0;
              const isLastSection =
                currentReadingSectionIndex >= readingSectionIds.length - 1;
              const isLastExtractInSection =
                currentExtractIndex >= totalExtracts - 1;
              console.log(
                "ReadingPage isLastQuestionInSimulation IELTS check:",
                {
                  currentReadingSectionIndex,
                  readingSectionIdsLength: readingSectionIds.length,
                  isLastSection,
                  currentExtractIndex,
                  totalExtracts,
                  isLastExtractInSection,
                }
              );
              return isLastSection && isLastExtractInSection;
            }

            // OET logic - check if we're on the last part and last extract
            const currentPartData = parts.find((p) => p.part === currentPart);
            if (!currentPartData) return false;

            // Check if we're on Part C (last part in OET)
            if (currentPart !== "C") return false;

            // Check if we're on the last extract in Part C
            const allQuestions = currentPartData?.questions || [];
            const uniqueExtractIds = [
              ...new Set(allQuestions.map((q) => q.readingTextId)),
            ];
            const isLastExtract =
              simulationExtractIndex >= uniqueExtractIds.length - 1;

            console.log("ReadingPage isLastQuestionInSimulation OET check:", {
              currentPart,
              simulationExtractIndex,
              uniqueExtractIdsLength: uniqueExtractIds.length,
              isLastExtract,
            });

            return isLastExtract;
          })()
        }
        onFinish={async (fromTimer) => {
          // On timer expiry OR user clicks Finish: submit and move to writing or show results
          const studentAttemptId = location.state?.studentAttemptId;

          // Handle SIMULATION mode finish
          if (!location.state?.isPracticeMode) {
            try {
              // Submit ALL questions with answers or empty strings
              let readingTestId = testId;
              if (localReadingData?.data?.readingTest?._id) {
                readingTestId = localReadingData.data.readingTest._id;
              }

              // Get all question IDs from the test
              const allQuestionIds = getAllQuestionIdsFromTest();

              // Build answers array with all questions
              const answersArray = allQuestionIds.map((questionId) => {
                const givenAnswer = answers?.[questionId] ?? "";
                return {
                  questionId: isNaN(Number(questionId)) ? questionId : Number(questionId),
                  givenAnswer: givenAnswer,
                };
              });

              const submissionData = {
                testId: readingTestId,
                testAttemptId: studentAttemptId,
                answers: answersArray,
                ...(readingStudentAnswerId &&
                  typeof readingStudentAnswerId === "string" &&
                  readingStudentAnswerId.trim() !== "" && {
                    studentAnswerId: readingStudentAnswerId,
                  }),
                isNextPart: true,
              };

              await submitTestAnswers(submissionData).unwrap();

              // Clear notes when reading section is completed
              setNotes([]);
              setNotesModalOpen(false);

              // Mark test attempt as completed
              if (studentAttemptId) {
                try {
                  await updateTestAttempt({
                    studentAttemptId,
                    isCompleted: true,
                  }).unwrap();
                  console.log("Simulation test attempt marked as completed");
                } catch (updateError) {
                  console.error(
                    "Error marking simulation test as completed:",
                    updateError
                  );
                }
              }

              // Show results popup (same as practice mode)
              await showPracticeResults(studentAttemptId);
            } catch (error) {
              console.error("Error in simulation onFinish:", error);
              // Still try to show results popup
              await showPracticeResults(studentAttemptId);
            }
            return;
          }

          // Handle PRACTICE mode finish
          if (location.state?.isPracticeMode) {
            const selectedSections = location.state?.selectedSections;

            try {
              // Submit ALL questions with answers or empty strings
              let readingTestId = testId;
              if (localReadingData?.data?.readingTest?._id) {
                readingTestId = localReadingData.data.readingTest._id;
              }

              // Get all question IDs from the test
              const allQuestionIds = getAllQuestionIdsFromTest();

              // Build answers array with all questions
              const answersArray = allQuestionIds.map((questionId) => {
                const givenAnswer = answers?.[questionId] ?? "";
                return {
                  questionId: isNaN(Number(questionId)) ? questionId : Number(questionId),
                  givenAnswer: givenAnswer,
                };
              });

              const submissionData = {
                testId: readingTestId,
                testAttemptId: studentAttemptId,
                answers: answersArray,
                ...(readingStudentAnswerId &&
                  typeof readingStudentAnswerId === "string" &&
                  readingStudentAnswerId.trim() !== "" && {
                    studentAnswerId: readingStudentAnswerId,
                  }),
                isNextPart: true,
              };

              await submitTestAnswers(submissionData).unwrap();

              // Clear notes when reading section is completed
              setNotes([]);
              setNotesModalOpen(false);

              // Check if writing is selected (must be explicitly true) - if so, navigate to writing
              if (selectedSections?.writing === true) {
                console.log(
                  "Reading finished, writing section is selected - navigating to writing"
                );

                // Navigate to writing section
                navigate(`${getBaseRoute()}/writing`, {
                  state: {
                    ...location.state,
                    currentSectionIndex:
                      (location.state?.currentSectionIndex || 0) + 1,
                    studentAnswerId: readingStudentAnswerId,
                    practiceData: null,
                  },
                });
                return; // Exit early - don't show results yet
              }

              // If no writing section or we've finished all sections
              // Mark test attempt as completed
              if (studentAttemptId) {
                try {
                  await updateTestAttempt({
                    studentAttemptId,
                    isCompleted: true,
                  }).unwrap();
                } catch (updateError) {
                  // Ignore update errors
                }
              }

              // Show practice results only if no writing section
              await showPracticeResults(studentAttemptId);
            } catch (error) {
              console.error("Error in onFinish:", error);
              // If error and writing is NOT selected, show results popup
              if (selectedSections?.writing !== true) {
                await showPracticeResults(studentAttemptId);
              }
            }
          }
        }}
      />

      {/* Test Results Popup for Practice Mode */}
      <TestResultsPopup
        open={showResultsPopup}
        onClose={() => setShowResultsPopup(false)}
        resultsData={testResults}
        onContinue={() =>
          navigate(location.state?.backRoute || "/app/mocktests")
        }
        attemptId={studentAttemptId}
        mockTestId={testId}
        baseRoute={location.state?.baseRoute || "/mock-test"}
        backRoute={location.state?.backRoute || "/app/mocktests"}
      />
    </>
  );
}
